import dayjs from "dayjs"
import { usePostMeetingRecordingMutation } from "features/management/api"
import { useLazyGetPresignedUrlsQuery, useLazyGetUploadIdQuery, usePostOnPresignedMutation } from "features/shared/api"
import { useBatchedUploadFromIndexedDB } from "hooks/useBatchedUploadFromIndexedDB"
import { useIndexedDB } from "hooks/useIndexedDB"
import { useCallback, useEffect, useRef, useState } from "react"
import RecordRTC, { StereoAudioRecorder } from "recordrtc"
import { createFixedMemoryBlobs } from "utils/audio/memoryBasedPartition"

import useSyncBlobs from "./useSyncBlobs"

const BLOB_COUNT = 5

export const uploadAndStoreBlobs = async ({
  uploadId,
  getPresignedUrls,
  postOnPresigned,
  totalParts = BLOB_COUNT,
  id,
}) => {
  const response = await getPresignedUrls(
    {
      uploadId: uploadId,
      fileName: `meetings/${id}`,
      partNumbers: totalParts,
    },
    true
  )

  if (response?.data) {
    return (audioBlob, audioBlobId, signal) => {
      return postOnPresigned({ query: response?.data?.[audioBlobId], data: audioBlob, signal })
    }
  }
}

export const useAudioRecorder = ({ id }) => {
  const [audioUrl, setAudioUrl] = useState(null)
  const recorderRef = useRef(null)
  const streamRef = useRef(null)
  const totalBlobsRef = useRef(0)
  const uploadIdRef = useRef(0)

  const { addDataIntoStore, getAllDataFromStore, deleteSingleDataEntryFromStore, editDataInStore } = useIndexedDB(
    "audioDB",
    "audio",
    null,
    true,
    [{ storeName: "processedAudio" }]
  )
  const { batchUploadAll } = useBatchedUploadFromIndexedDB("audioDB", "processedAudio", 10, "audioMetadata")

  const [getUploadId] = useLazyGetUploadIdQuery()
  const [getPresignedUrls] = useLazyGetPresignedUrlsQuery()
  const [postOnPresigned] = usePostOnPresignedMutation()
  const [postMeetingRecording] = usePostMeetingRecordingMutation() // this should be handled through inversion of control to keep the hook generic

  const isRecording = useRef(false)
  const lockedRef = useRef(false)
  const intervalRef = useRef(null)
  const setElapsedTimeRef = useRef(null)

  const { syncData } = useSyncBlobs({
    finalUploadQuery: postMeetingRecording,
    deleteOnFail: true,
  })

  let getMetaData = useCallback(
    async dataKey => {
      const uploadIdData = await getAllDataFromStore(null, "audioMetadata")
      return dataKey ? uploadIdData?.[0]?.[dataKey] : uploadIdData?.[0]
    },
    [getAllDataFromStore]
  )

  const batchUploadBlobs = async ({ uploadId, getPresignedUrls, postOnPresigned, meetingId, blobIdString }) => {
    const batchUploadQuery = await uploadAndStoreBlobs({
      uploadId,
      getPresignedUrls,
      postOnPresigned,
      id: meetingId,
      totalParts: blobIdString,
    })
    await batchUploadAll({ batchQuery: batchUploadQuery })
  }

  const handleDataAvailable = useCallback(
    async (blob, finalBlob = false) => {
      if (blob) {
        await addDataIntoStore({ blob })
      }

      const totalBlobs = (await getAllDataFromStore()) ?? []
      let blobIdStringOfUnprocessedBlobs = totalBlobs?.map(item => item?.id)

      totalBlobsRef.current = totalBlobs.length
      let blobList = totalBlobs?.sort((a, b) => a?.id - b?.id)?.map(item => item?.blob)

      let combinedBlobs = await createFixedMemoryBlobs(blobList, 1.85 * 1024 * 1024, finalBlob)

      combinedBlobs?.forEach(async item => {
        await addDataIntoStore(item, "processedAudio")
      })

      if (combinedBlobs?.length) {
        blobIdStringOfUnprocessedBlobs?.forEach(async item => await deleteSingleDataEntryFromStore(item))
      }

      const totalProcessedBlobs = await getAllDataFromStore(null, "processedAudio")
      let blobIdString = totalProcessedBlobs?.map(item => item?.id)?.toString()

      const uploadIdData = await getAllDataFromStore(null, "audioMetadata")
      uploadIdRef.current = uploadIdData?.[0]?.uploadId
      let uploadId = uploadIdRef.current
      let meetingId = uploadIdData?.[0]?.meetingId

      if (totalProcessedBlobs?.length && totalProcessedBlobs?.length >= BLOB_COUNT && !lockedRef?.current) {
        lockedRef.current = true
        await batchUploadBlobs({ uploadId, getPresignedUrls, postOnPresigned, meetingId, blobIdString })
        lockedRef.current = false
      } else if (finalBlob) {
        await batchUploadBlobs({ uploadId, getPresignedUrls, postOnPresigned, meetingId, blobIdString })
      }
    },
    [
      addDataIntoStore,
      getAllDataFromStore,
      batchUploadAll,
      getPresignedUrls,
      postOnPresigned,
      deleteSingleDataEntryFromStore,
    ]
  )

  const startRecording = useCallback(
    async (uploadIdExist = false) => {
      if (!recorderRef.current) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
          streamRef.current = stream

          recorderRef.current = new RecordRTC(stream, {
            type: "audio",
            mimeType: "audio/wav",
            desiredSampRate: 16000,
            numberOfAudioChannels: 1,
            timeSlice: 5000,
            recorderType: StereoAudioRecorder,
            ondataavailable: handleDataAvailable,
          })

          if (uploadIdExist) {
            recorderRef.current.startRecording()
            isRecording.current = true
            return
          }

          if (!uploadIdExist) {
            getUploadId({ fileName: `meetings/${id}`, fileType: "audio/wav" })
              .unwrap()
              .then(async value => {
                addDataIntoStore(
                  { uploadId: value.uploadId, meetingId: id, timeStamp: dayjs().unix() },
                  "audioMetadata"
                )

                recorderRef.current.startRecording()
                isRecording.current = true
              })
              .catch(error => console.error("Error getting upload ID:", error))
          }
        } catch (error) {
          console.error("Error accessing microphone:", error)
        }
      }
    },
    [handleDataAvailable, addDataIntoStore, getUploadId, id]
  )

  const stopRecording = useCallback(async () => {
    if (!recorderRef.current) return

    isRecording.current = false

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }

    try {
      await new Promise((resolve, reject) => {
        try {
          recorderRef.current.stopRecording(async () => {
            try {
              let metaObject = await getMetaData()
              await editDataInStore({ ...metaObject, isFinished: true }, null, "audioMetadata")
              await handleDataAvailable(null, true)
              resolve(null)
            } catch (err) {
              reject(err)
            } finally {
              recorderRef.current.destroy()
              recorderRef.current = null
            }
          })
        } catch (error) {
          reject(error)
        }
      })

      await syncData()
    } catch (error) {
      console.error("Error stopping recorder:", error)
      // Cleanup in case of failure
      if (recorderRef.current) {
        recorderRef.current.destroy()
        recorderRef.current = null
      }
    }
  }, [syncData, handleDataAvailable, getMetaData, editDataInStore])

  let startRecordingOnRefresh = useCallback(() => {
    let restartRecording = async () => {
      let metaObject = await getMetaData()

      if (metaObject?.meetingId == id && metaObject?.uploadId && !metaObject?.isFinished) {
        await startRecording(true)
      }
    }

    if (!isRecording?.current && id) {
      restartRecording()
    }
  }, [id, getMetaData, startRecording])

  useEffect(() => {
    startRecordingOnRefresh()
  }, [id])

  useEffect(() => {
    if (isRecording?.current) {
      const initializeElapsedTime = async () => {
        let currentTime = await getMetaData("timeStamp")
        setElapsedTimeRef.current = 3600 - (dayjs().unix() - currentTime)
      }
      initializeElapsedTime()
    }
  }, [getMetaData]) // Removed isRecording.current dependency

  // Effect for countdown timer
  useEffect(() => {
    if (isRecording?.current) {
      intervalRef.current = setInterval(() => {
        if (setElapsedTimeRef?.current == 1) {
          clearInterval(intervalRef.current)
          stopRecording()
          setElapsedTimeRef.current = 0
        } else {
          setElapsedTimeRef.current = setElapsedTimeRef.current - 1
        }
      }, 1000)
    }

    return () => {
      clearInterval(intervalRef.current)
    }
  }, [stopRecording]) // Removed isRecording.current dependency

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
        streamRef.current = null
        isRecording.current = false
      }
    }
  }, [])

  return { audioUrl, startRecording, stopRecording, getMetaData, syncData }
}
